<template>
    <div class="centrinvest">
        <TheHeader />
        <main
            id="section-to-print"
            class="main"
        >
            <Transition
                name="fade"
                mode="out-in"
            >
                <Nuxt />
            </Transition>
        </main>
        <TheFooter />
        <FloatingChat />
        <UILoader />
        <ClientOnly>
            <Popup
                :visibility="isPopupVisible && isPopupShown"
                @hide="onPopupCloseHandler(false)"
            >
                <component
                    :is="visiblePopup"
                    @hide="onPopupCloseHandler(true)"
                />
            </Popup>
            <UserRegionQuestionModal />
            <ToastContainer />
        </ClientOnly>
    </div>
</template>

<script>
import {defineComponent, computed, onMounted, onBeforeUnmount, ref, nextTick} from 'vue';
import {setCssVariablesProperty} from '@cibsite/ui-components-vue2';
import {useContext} from '@nuxtjs/composition-api';

import {POPUP_TYPES} from '@/core/models/common/PopupsModel';
import TheHeader from '@/components/Sections/Header/TheHeader.vue';
import TheFooter from '@/components/Sections/Footer/TheFooter.vue';
import FloatingChat from '@/components/FloatingChat/FloatingChat.vue';
import Popup from '@/components/Popups/Popup.vue';
import UILoader from '@/components/UI-components/Loader/Loader.vue';
import SubscribePopup from '@/components/Popups/SubscribePopup.vue';
import UserRegionQuestionModal from '@/components/UserRegions/UserRegionQuestionModal.vue';
import ToastContainer from '@/components/Toast/ToastContainer.vue';
import {useRegionsStore} from '@/stores/regions';
import {usePopupsStore} from '@/stores/popups';
import {useLastRefererCookie} from '@/core/composables/useCookie';
import {datePlugin} from '@/core/plugins/dates';
import DEFAULT_CSS_VARIABLES from '@/core/constants/defaultCssVariables';
import {useLoaderStore} from '@/stores/loader';
import {useLayoutHead} from '@/core/composables/useLayoutHead';

const ALLOWED_ACTIONS = [
    'navigation',
    'detectUserCoords',
    'detectUserLocationByCoords',
];

const isActionAllowed = (actionType) => {
    return ALLOWED_ACTIONS.some((action) => actionType.includes(action));
};

export default defineComponent({
    name: 'DefaultLayout',
    components: {
        TheHeader,
        TheFooter,
        FloatingChat,
        Popup,
        UILoader,
        SubscribePopup,
        UserRegionQuestionModal,
        ToastContainer,
    },
    scrollToTop: true,
    setup() {
        const {getHeadMetaForPage} = useLayoutHead();
        const regionsStore = useRegionsStore();
        const popupsStore = usePopupsStore();
        const {initLastRefererCookie} = useLastRefererCookie();
        const loader = useLoaderStore();

        const headMeta = ref(null);
        const unsubscribe = ref(null);
        const isPopupVisible = ref(false);
        const visiblePopup = ref('');
        const {$isMobile} = useContext();

        const isPopupShown = computed(() => popupsStore.isPopupShown);

        onMounted(() => {
            init();
            initLastRefererCookie();
        });

        onBeforeUnmount(() => {
            typeof unsubscribe.value === 'function' && unsubscribe.value();
            window.removeEventListener('load', onWindowLoadHandler);
        });

        function init() {
            unsubscribe.value = regionsStore.$onAction(({name, after}) => {
                if (!isActionAllowed(name)) {
                    !loader?.isActive && loader.show();
                }

                after(() => {
                    if (!isActionAllowed(name)) {
                        loader?.isActive && loader.hide();
                    }
                });
            });

            nextTick().then(() => {
                window && window.addEventListener('load', onWindowLoadHandler);
            });
        }

        function onPopupCloseHandler(isSubscribe) {
            isPopupVisible.value = false;
            if (isSubscribe) {
                setSubscribePopupDelay(POPUP_TYPES.SUBSCRIBE.visibilityDelayAfterSubscribe);

                return;
            }

            setSubscribePopupDelay(POPUP_TYPES.SUBSCRIBE.visibilityDelayAfterClose);
        }

        async function onWindowLoadHandler() {
            if ($isMobile) {
                return;
            }

            const subscribePopupDelay = localStorage.getItem('subscribePopupDelay');
            const timeDiff = subscribePopupDelay ? getTimeDifference(JSON.parse(subscribePopupDelay)) : 0;

            if (!subscribePopupDelay || timeDiff <= 0) {
                await showPopup({
                    popup: POPUP_TYPES.SUBSCRIBE.name,
                    delay: 3000,
                });

                setTimeout(() => {
                    if (isPopupVisible.value) {
                        isPopupVisible.value = false;
                        setSubscribePopupDelay(POPUP_TYPES.SUBSCRIBE.visibilityDelayAfterInaction);
                    }
                }, POPUP_TYPES.SUBSCRIBE.delayBeforeClose);
            }
        }

        function getTimeDifference(time) {
            const startTime = datePlugin();
            const endTime = datePlugin(time);

            return endTime.diff(startTime, 'hours');
        }

        function setSubscribePopupDelay(delay) {
            const subscribePopupDelay = datePlugin().add(delay, 'hour').format();

            localStorage.setItem('subscribePopupDelay', JSON.stringify(subscribePopupDelay));
        }

        function showPopup({popup, delay = 1000}) {
            return new Promise((resolve) => {
                setTimeout(() => {
                    visiblePopup.value = popup;
                    isPopupVisible.value = true;
                    resolve();
                }, delay);
            });
        }

        return {
            headMeta,
            visiblePopup,
            isPopupVisible,
            isPopupShown,
            onPopupCloseHandler,
            getHeadMetaForPage,
        };
    },
    asyncData() {
        const regionsStore = useRegionsStore();
        regionsStore.checkUserLocationCookie();
    },
    head() {
        return this.headMeta;
    },
    watch: {
        $route: {
            immediate: true,
            handler(newRoute) {
                this.headMeta = this.getHeadMetaForPage(newRoute.path);

                if (process.client) {
                    setCssVariablesProperty(DEFAULT_CSS_VARIABLES);
                }
            },
        },
    },
});
</script>

<style scoped lang="scss">
.main {
    min-height: 500px;
}
</style>
